import { Checkbox } from '@tebuto/components/ui/checkbox'
import Description from '@tebuto/forms/Description'
import Mandatory from '@tebuto/forms/Mandatory'
import Suffix from '@tebuto/forms/Suffix'
import { cn } from '@tebuto/lib/utils'
import { useField } from 'formik'
import { type CSSProperties, type JSX, type ReactNode } from 'react'
import ErrorMessage from './ErrorMessage'

interface FormCheckboxProps {
    name: string
    label?: string | ReactNode
    className?: string
    suffix?: string
    mandatory?: boolean
    description?: string
    disabled?: boolean
    showErrors?: boolean
    /** Checked fill/border color (e.g. therapist theme primary). */
    accentColor?: string
    onCheckedChange?: (checked: boolean) => void
}

export default function FormCheckbox({
    className = '',
    label,
    name,
    suffix,
    description,
    mandatory,
    disabled = false,
    showErrors,
    accentColor,
    onCheckedChange: onCheckedChangeCallback
}: FormCheckboxProps): JSX.Element {
    const [field, , helpers] = useField({ name, type: 'checkbox' })

    return (
        <div className={cn('relative', className)}>
            <div className="flex items-start gap-3">
                <Checkbox
                    id={name}
                    checked={field.value ?? false}
                    onCheckedChange={(checked) => {
                        helpers.setValue(checked)
                        if (typeof checked === 'boolean') {
                            onCheckedChangeCallback?.(checked)
                        }
                    }}
                    disabled={disabled}
                    style={
                        accentColor
                            ? ({
                                  '--form-checkbox-accent': accentColor
                              } as CSSProperties)
                            : undefined
                    }
                    className={cn(
                        'h-5 w-5',
                        disabled && 'cursor-not-allowed',
                        accentColor &&
                            'focus-visible:border-[var(--form-checkbox-accent)] focus-visible:ring-[color-mix(in_srgb,var(--form-checkbox-accent)_20%,transparent)] data-[state=checked]:border-[var(--form-checkbox-accent)] data-[state=checked]:bg-[var(--form-checkbox-accent)]'
                    )}
                />
                <div>
                    {label && (
                        <label
                            htmlFor={name}
                            className={cn(
                                'text-sm font-medium text-foreground-secondary cursor-pointer',
                                disabled && 'cursor-not-allowed opacity-50'
                            )}
                        >
                            {label} <Mandatory mandatory={mandatory} />
                        </label>
                    )}
                    <Description description={description} className="ml-0" />
                </div>
                <Suffix suffix={suffix} />
            </div>
            {showErrors && <ErrorMessage name={name} />}
        </div>
    )
}
